import React, { useState, Fragment, Suspense, lazy } from 'react';
import { LayoutDashboard, Users, Star, FileText, ShoppingBag, Settings, LogOut, Search, Plus, Edit2, Trash2, MoreVertical, CheckCircle2, XCircle, TrendingUp, DollarSign, Calendar, Image as ImageIcon, Globe, Bell, Mail, Shield, Eye, Ban, UserCheck, MessageSquare, Receipt, Wallet, Megaphone, AlertTriangle, Send, Clock, CreditCard, ArrowUpRight, ArrowDownRight, Filter, RefreshCw, Copy, ExternalLink, BarChart3, PieChart, Target, Percent, Tag, Volume2, UserCircle, ShieldCheck, CalendarDays, Repeat, MonitorPlay, Zap, Store, Gift, Check, Download, X, Save, ChevronDown, ChevronRight, ChevronUp, Activity, ArrowRight, Code, Video, HeartPulse, Layers, BookOpen } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Tabs } from '../components/ui/Tabs';
import { useToast } from '../components/ui/Toast';
import { useUser } from '../contexts/UserContext';
import { AdminSectionBoundary } from '../components/admin/AdminSectionBoundary';
import { initialUsers, initialAstrologers, createInitialOrders, createInitialTickets, createInitialReviews, initialComplaints, initialRefunds, initialVerificationQueue, initialAdRequests, createInitialSubscriptions, initialCampaigns, initialContentItems, initialPromoCodes, initialBanners, initialServices, initialCalendarEvents } from '../data/adminMockData';
import { AdminDetailPanel } from '../components/admin/AdminDetailPanel';
import { AdminCreatePanel } from '../components/admin/AdminCreatePanel';
import { AdminMarketingManager } from '../components/admin/AdminMarketingManager';
import { AdminProfilesManager } from '../components/admin/AdminProfilesManager';
import { AdminServicesManager } from '../components/admin/AdminServicesManager';
import { AdminCalendarEventsManager } from '../components/admin/AdminCalendarEventsManager';
// ═══════════════════════════════════════════
// LAZY ADMIN COMPONENTS
// Each tab loads only when selected — saves ~20k lines from initial bundle
// ═══════════════════════════════════════════
const CmsManager = lazy(() => import('../components/admin/CmsManager').then(m => ({
  default: m.CmsManager
})));
const AnalyticsDashboard = lazy(() => import('../components/admin/AnalyticsDashboard').then(m => ({
  default: m.AnalyticsDashboard
})));
const AdminMessaging = lazy(() => import('../components/admin/AdminMessaging').then(m => ({
  default: m.AdminMessaging
})));
const AdminDashboard = lazy(() => import('../components/admin/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const AdminUsersManager = lazy(() => import('../components/admin/AdminUsersManager').then(m => ({
  default: m.AdminUsersManager
})));
const AdminAstrologersManager = lazy(() => import('../components/admin/AdminAstrologersManager').then(m => ({
  default: m.AdminAstrologersManager
})));
const AdminVerificationManager = lazy(() => import('../components/admin/AdminVerificationManager').then(m => ({
  default: m.AdminVerificationManager
})));
const AdminOrdersManager = lazy(() => import('../components/admin/AdminOrdersManager').then(m => ({
  default: m.AdminOrdersManager
})));
const AdminAIReportsManager = lazy(() => import('../components/admin/AdminAIReportsManager').then(m => ({
  default: m.AdminAIReportsManager
})));
const AdminAdvertisingFullManager = lazy(() => import('../components/admin/AdminAdvertisingFullManager').then(m => ({
  default: m.AdminAdvertisingFullManager
})));
const AdminFinanceFullManager = lazy(() => import('../components/admin/AdminFinanceFullManager').then(m => ({
  default: m.AdminFinanceFullManager
})));
const AdminSubscriptionsFullManager = lazy(() => import('../components/admin/AdminSubscriptionsFullManager').then(m => ({
  default: m.AdminSubscriptionsFullManager
})));
const AdminConsultationsManager = lazy(() => import('../components/admin/AdminConsultationsManager').then(m => ({
  default: m.AdminConsultationsManager
})));
const AdminChatMonitor = lazy(() => import('../components/admin/AdminChatMonitor').then(m => ({
  default: m.AdminChatMonitor
})));
const AdminModerationCenter = lazy(() => import('../components/admin/AdminModerationCenter').then(m => ({
  default: m.AdminModerationCenter
})));
const AdminSettingsFullManager = lazy(() => import('../components/admin/AdminSettingsFullManager').then(m => ({
  default: m.AdminSettingsFullManager
})));
const AdminRolesManager = lazy(() => import('../components/admin/AdminRolesManager').then(m => ({
  default: m.AdminRolesManager
})));
const AdminAuditLog = lazy(() => import('../components/admin/AdminAuditLog').then(m => ({
  default: m.AdminAuditLog
})));
const AdminNotificationsCenter = lazy(() => import('../components/admin/AdminNotificationsCenter').then(m => ({
  default: m.AdminNotificationsCenter
})));
const AdminFullChat = lazy(() => import('../components/admin/AdminFullChat').then(m => ({
  default: m.AdminFullChat
})));
const AdminSystemHealth = lazy(() => import('../components/admin/AdminSystemHealth').then(m => ({
  default: m.AdminSystemHealth
})));
const AdminMarketplaceManager = lazy(() => import('../components/admin/AdminMarketplaceManager').then(m => ({
  default: m.AdminMarketplaceManager
})));
interface AdminPageProps {
  onNavigate: (page: string) => void;
}
export function AdminPage({
  onNavigate
}: AdminPageProps) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const {
    success
  } = useToast();
  const {
    logout
  } = useUser();
  // --- UI STATES ---
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [expandedRows, setExpandedRows] = useState<Record<string, string | number | null>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');
  const [showGlobalResults, setShowGlobalResults] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<'Сегодня' | 'Вчера' | '7 дней' | '30 дней' | 'Квартал' | 'Год'>('Сегодня');
  const [detailPanel, setDetailPanel] = useState<{
    type: string;
    data: any;
    title?: string;
  } | null>(null);
  const [createPanel, setCreatePanel] = useState<{
    type: string;
    title: string;
  } | null>(null);
  // --- CREATE FORM STATES ---
  const [newContentForm, setNewContentForm] = useState({
    title: '',
    type: 'Статья блога',
    status: 'Черновик'
  });
  const [newServiceForm, setNewServiceForm] = useState({
    title: '',
    price: '',
    type: 'Авто-отчёт'
  });
  const [newEventForm, setNewEventForm] = useState({
    title: '',
    type: 'Затмение',
    date: '',
    zodiac: ''
  });
  const [newPromoForm, setNewPromoForm] = useState({
    code: '',
    discount: '',
    limit: '',
    expires: ''
  });
  const [newCampaignForm, setNewCampaignForm] = useState({
    name: '',
    subject: '',
    recipients: ''
  });
  const [notifSettings, setNotifSettings] = useState({
    newOrders: true,
    newAstrologers: true,
    pushNotifications: false,
    weeklyDigest: true
  });
  const [paymentSystems, setPaymentSystems] = useState([{
    name: 'ЮKassa',
    status: true
  }, {
    name: 'Stripe',
    status: false
  }, {
    name: 'Тинькофф',
    status: true
  }]);
  const TRACKING_STORAGE_KEY = 'astriqo_tracking_codes';
  const [trackingCodes, setTrackingCodes] = useState<{
    id: string;
    name: string;
    placeholder: string;
    icon: string;
    color: string;
    value: string;
    enabled: boolean;
    description: string;
  }[]>(() => {
    try {
      const saved = localStorage.getItem(TRACKING_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    return [{
      id: 'ga4',
      name: 'Google Analytics 4',
      placeholder: 'G-XXXXXXXXXX',
      icon: '📊',
      color: 'bg-blue-50 border-blue-200 text-blue-700',
      value: '',
      enabled: false,
      description: 'Measurement ID из Google Analytics'
    }, {
      id: 'ym',
      name: 'Яндекс Метрика',
      placeholder: '12345678',
      icon: '🔴',
      color: 'bg-amber-50 border-amber-200 text-amber-700',
      value: '',
      enabled: false,
      description: 'Номер счётчика Яндекс Метрики'
    }, {
      id: 'gtm',
      name: 'Google Tag Manager',
      placeholder: 'GTM-XXXXXXX',
      icon: '🏷️',
      color: 'bg-indigo-50 border-indigo-200 text-indigo-700',
      value: '',
      enabled: false,
      description: 'ID контейнера GTM'
    }, {
      id: 'meta',
      name: 'Meta Pixel (Facebook)',
      placeholder: '123456789012345',
      icon: '📘',
      color: 'bg-sky-50 border-sky-200 text-sky-700',
      value: '',
      enabled: false,
      description: 'Pixel ID из Meta Business Suite'
    }, {
      id: 'vk',
      name: 'VK Pixel',
      placeholder: 'VK-RTRG-123456-xxxxx',
      icon: '💬',
      color: 'bg-blue-50 border-blue-200 text-blue-700',
      value: '',
      enabled: false,
      description: 'Pixel ID из VK Рекламы'
    }, {
      id: 'custom_head',
      name: 'Свой код (head)',
      placeholder: '<script>...</script>',
      icon: '🧩',
      color: 'bg-slate-50 border-slate-200 text-slate-700',
      value: '',
      enabled: false,
      description: 'Произвольный код в <head> сайта'
    }];
  });
  const saveTrackingCodes = (updated: typeof trackingCodes) => {
    setTrackingCodes(updated);
    try {
      localStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(updated));
    } catch {}
  };
  const updateTrackingCode = (id: string, field: 'value' | 'enabled', val: string | boolean) => {
    const updated = trackingCodes.map(t => t.id === id ? {
      ...t,
      [field]: val
    } : t);
    saveTrackingCodes(updated);
  };
  const [showTrackingSetup, setShowTrackingSetup] = useState(true);
  const [newAstrologerForm, setNewAstrologerForm] = useState({
    name: '',
    email: '',
    spec: 'Западная астрология'
  });
  const [newOrderForm, setNewOrderForm] = useState({
    client: '',
    service: 'Натальная карта',
    astrologer: '',
    amount: ''
  });
  const [newAiReportForm, setNewAiReportForm] = useState({
    type: 'Натальная карта',
    client: '',
    birthDate: '',
    birthPlace: ''
  });
  const [newAdForm, setNewAdForm] = useState({
    name: '',
    type: 'Баннер',
    budget: '',
    placement: 'Главная страница'
  });
  // --- MOCK DATA ---
  const [users, setUsers] = useState(initialUsers);
  const [astrologers, setAstrologers] = useState(initialAstrologers);
  const [orders, setOrders] = useState(() => createInitialOrders(initialUsers, initialAstrologers));
  const [tickets, setTickets] = useState(() => createInitialTickets(initialUsers));
  const [reviews, setReviews] = useState(() => createInitialReviews(initialUsers, initialAstrologers));
  const [complaints, setComplaints] = useState(initialComplaints);
  const [refunds, setRefunds] = useState(initialRefunds);
  const [verificationQueue, setVerificationQueue] = useState(initialVerificationQueue);
  const [adRequests, setAdRequests] = useState(initialAdRequests);
  const [userSubscriptions, setUserSubscriptions] = useState(() => createInitialSubscriptions(initialUsers));
  const [campaigns, setCampaigns] = useState(initialCampaigns);
  const [contentItems, setContentItems] = useState(initialContentItems);
  const [promoCodes, setPromoCodes] = useState(initialPromoCodes);
  const [banners, setBanners] = useState(initialBanners);
  const [services, setServices] = useState(initialServices);
  const [calendarEvents, setCalendarEvents] = useState(initialCalendarEvents);
  // --- HELPER ---
  const generateCSV = (data: any[], name: string) => {
    success(`Экспорт ${name}.csv начат (${data.length} записей)`);
  };
  // --- HANDLERS ---
  const handleCompleteOrder = (id: string) => {
    setOrders(orders.map(o => o.id === id ? {
      ...o,
      status: 'completed'
    } : o));
    success(`Заказ завершён`);
  };
  const handlePublishEvent = (id: number) => {
    setCalendarEvents(calendarEvents.map(e => e.id === id ? {
      ...e,
      status: 'published'
    } : e));
    success('Событие опубликовано');
  };
  const handleDeletePromo = (id: number) => {
    setPromoCodes(promoCodes.filter(p => p.id !== id));
    success('Промокод удалён');
  };
  const handleDeleteBanner = (id: number) => {
    setBanners(banners.filter(b => b.id !== id));
    success('Баннер удалён');
  };
  const handleDeleteService = (id: number) => {
    setServices(services.filter(s => s.id !== id));
    success('Услуга удалена');
  };
  const handleAdRequestStatus = (id: string, newStatus: string) => {
    setAdRequests(adRequests.map(r => r.id === id ? {
      ...r,
      status: newStatus
    } : r));
    success('Статус обновлён');
  };
  const handleCancelSubscription = (id: number) => {
    setUserSubscriptions(userSubscriptions.map(s => s.id === id ? {
      ...s,
      status: 'cancelled'
    } : s));
    success('Подписка отменена');
  };
  // --- CREATE HANDLERS ---
  const handleCreateContent = () => {
    if (!newContentForm.title.trim()) return;
    setContentItems([{
      id: Date.now(),
      title: newContentForm.title,
      type: newContentForm.type,
      status: newContentForm.status,
      date: 'Сегодня'
    }, ...contentItems]);
    setCreatePanel(null);
    setActiveTab('content');
    success(`Контент «${newContentForm.title}» создан → Раздел "Контент"`);
    setNewContentForm({
      title: '',
      type: 'Статья блога',
      status: 'Черновик'
    });
  };
  const handleCreateService = () => {
    if (!newServiceForm.title.trim()) return;
    setServices([...services, {
      id: Date.now(),
      title: newServiceForm.title,
      price: newServiceForm.price,
      type: newServiceForm.type,
      active: true
    }]);
    setCreatePanel(null);
    setActiveTab('services');
    success(`Услуга «${newServiceForm.title}» добавлена → Раздел "Услуги"`);
    setNewServiceForm({
      title: '',
      price: '',
      type: 'Авто-отчёт'
    });
  };
  const handleCreateEvent = () => {
    if (!newEventForm.title.trim()) return;
    setCalendarEvents([{
      id: Date.now(),
      title: newEventForm.title,
      type: newEventForm.type,
      date: newEventForm.date,
      zodiac: newEventForm.zodiac,
      status: 'draft',
      views: '0'
    }, ...calendarEvents]);
    setCreatePanel(null);
    setActiveTab('calendar_events');
    success(`Событие «${newEventForm.title}» добавлено → Раздел "Календарь"`);
    setNewEventForm({
      title: '',
      type: 'Затмение',
      date: '',
      zodiac: ''
    });
  };
  const handleCreatePromo = () => {
    if (!newPromoForm.code.trim()) return;
    setPromoCodes([{
      id: Date.now(),
      code: newPromoForm.code.toUpperCase(),
      discount: newPromoForm.discount,
      limit: newPromoForm.limit || '∞',
      used: 0,
      expires: newPromoForm.expires || 'Бессрочно',
      status: 'active'
    }, ...promoCodes]);
    setCreatePanel(null);
    setActiveTab('marketing');
    success(`Промокод «${newPromoForm.code.toUpperCase()}» создан → Раздел "Маркетинг"`);
    setNewPromoForm({
      code: '',
      discount: '',
      limit: '',
      expires: ''
    });
  };
  const handleCreateCampaign = () => {
    if (!newCampaignForm.name.trim()) return;
    setCampaigns([{
      id: Date.now(),
      name: newCampaignForm.name,
      subject: newCampaignForm.subject,
      recipients: newCampaignForm.recipients || '0',
      sent: '0',
      opened: '0%',
      clicked: '0%',
      date: 'Сегодня',
      status: 'draft'
    }, ...campaigns]);
    setCreatePanel(null);
    setActiveTab('marketing');
    success(`Рассылка «${newCampaignForm.name}» создана → Раздел "Маркетинг"`);
    setNewCampaignForm({
      name: '',
      subject: '',
      recipients: ''
    });
  };
  const handleCreateAstrologer = () => {
    if (!newAstrologerForm.name.trim()) return;
    setAstrologers([...astrologers, {
      id: Date.now(),
      name: newAstrologerForm.name,
      email: newAstrologerForm.email,
      spec: newAstrologerForm.spec,
      rating: 0,
      reviews: 0,
      orders: 0,
      earnings: '0 ₽',
      status: 'pending',
      verified: false,
      badge: null
    }]);
    setCreatePanel(null);
    setActiveTab('astrologers');
    success(`Астролог «${newAstrologerForm.name}» добавлен → Раздел "Астрологи"`);
    setNewAstrologerForm({
      name: '',
      email: '',
      spec: 'Натальная астрология'
    });
  };
  const handleCreateOrder = () => {
    if (!newOrderForm.client.trim()) return;
    const newId = `ORD-${Date.now().toString().slice(-4)}`;
    setOrders([{
      id: newId,
      client: newOrderForm.client,
      email: '',
      service: newOrderForm.service,
      astrologer: newOrderForm.astrologer || astrologers[0]?.name || '',
      amount: newOrderForm.amount || '3 900 ₽',
      status: 'in_progress',
      date: 'Сегодня',
      payment: 'paid'
    }, ...orders]);
    setCreatePanel(null);
    setActiveTab('orders');
    success(`Заказ «${newId}» создан → Раздел "Заказы"`);
    setNewOrderForm({
      client: '',
      service: 'Натальная карта',
      astrologer: '',
      amount: ''
    });
  };
  const handleCreateAiReport = () => {
    if (!newAiReportForm.client.trim()) return;
    const reportId = `RPT-${Date.now().toString().slice(-4)}`;
    setCreatePanel(null);
    setActiveTab('ai_reports');
    success(`AI отчёт «${reportId}» (${newAiReportForm.type}) создан → Раздел "AI отчёты"`);
    setNewAiReportForm({
      type: 'Натальная карта',
      client: '',
      birthDate: '',
      birthPlace: ''
    });
  };
  const handleCreateAd = () => {
    if (!newAdForm.name.trim()) return;
    setAdRequests([{
      id: `AD-${Date.now().toString().slice(-4)}`,
      advertiser: 'Администратор',
      type: newAdForm.type,
      placement: newAdForm.placement,
      budget: newAdForm.budget || '0 ₽',
      status: 'pending',
      date: 'Сегодня'
    }, ...adRequests]);
    setCreatePanel(null);
    setActiveTab('advertising');
    success(`Рекламная кампания «${newAdForm.name}» создана → Раздел "Реклама"`);
    setNewAdForm({
      name: '',
      type: 'Баннер',
      budget: '',
      placement: 'Главная страница'
    });
  };
  // --- RENDER HELPERS ---
  const renderTableHeader = (title: string, count: number, onExport?: () => void, onCreate?: () => void, createLabel?: string) => <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-16326">
      <div data-id="element-16327">
        <h2 className="text-lg font-bold text-slate-900" data-id="element-16328">{title}</h2>
        <p className="text-xs text-slate-500" data-id="element-16329">{count} записей</p>
      </div>
      <div className="flex items-center gap-2" data-id="element-16330">
        {onExport && <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-16332" />} onClick={onExport} data-id="element-16331">
            Экспорт
          </Button>}
        {onCreate && <Button variant="primary" size="sm" icon={<Plus className="w-4 h-4" data-id="element-16334" />} onClick={onCreate} data-id="element-16333">
            {createLabel || 'Создать'}
          </Button>}
      </div>
    </div>;
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
      case 'completed':
        return <Badge variant="success" size="sm" data-id="element-16335">
            {status === 'active' ? 'Активен' : 'Завершён'}
          </Badge>;
      case 'blocked':
        return <Badge variant="error" size="sm" data-id="element-16336">
            Заблокирован
          </Badge>;
      case 'pending':
        return <Badge variant="warning" size="sm" data-id="element-16337">
            Ожидает
          </Badge>;
      default:
        return <Badge variant="neutral" size="sm" data-id="element-16338">
            {status}
          </Badge>;
    }
  };
  // --- CONTENT SWITCH ---
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <AdminDashboard users={users} orders={orders} astrologers={astrologers} tickets={tickets} reviews={reviews} complaints={complaints} refunds={refunds} verificationQueue={verificationQueue} adRequests={adRequests} userSubscriptions={userSubscriptions} campaigns={campaigns} setActiveTab={setActiveTab} setCreatePanel={setCreatePanel} selectedPeriod={selectedPeriod} data-id="element-16339" />;
      case 'users':
        return <AdminUsersManager users={users} orders={orders} astrologers={astrologers} setActiveTab={setActiveTab} setDetailPanel={setDetailPanel} setCreatePanel={setCreatePanel} success={success} data-id="element-16340" />;
      case 'profiles':
        return <AdminProfilesManager astrologers={astrologers} renderStatusBadge={renderStatusBadge} renderTableHeader={renderTableHeader} generateCSV={generateCSV} data-id="element-16341" />;
      case 'astrologers':
        return <AdminAstrologersManager astrologers={astrologers} users={users} orders={orders} setActiveTab={setActiveTab} setDetailPanel={setDetailPanel} setCreatePanel={setCreatePanel} success={success} data-id="element-16342" />;
      case 'verification':
        return <AdminVerificationManager verificationQueue={verificationQueue} astrologers={astrologers} setActiveTab={setActiveTab} setDetailPanel={setDetailPanel} success={success} data-id="element-16343" />;
      case 'orders':
        return <AdminOrdersManager orders={orders} users={users} astrologers={astrologers} setActiveTab={setActiveTab} setDetailPanel={setDetailPanel} setCreatePanel={setCreatePanel} success={success} data-id="element-16344" />;
      case 'consultations':
        return <AdminConsultationsManager consultations={[]} users={users} astrologers={astrologers} setActiveTab={setActiveTab} setDetailPanel={setDetailPanel} success={success} data-id="element-16345" />;
      case 'ai_reports':
        return <AdminAIReportsManager users={users} astrologers={astrologers} setActiveTab={setActiveTab} setCreatePanel={setCreatePanel} setDetailPanel={setDetailPanel} success={success} data-id="element-16346" />;
      case 'finance':
        return <AdminFinanceFullManager orders={orders} refunds={refunds} users={users} astrologers={astrologers} setActiveTab={setActiveTab} success={success} data-id="element-16347" />;
      case 'subscriptions':
        return <AdminSubscriptionsFullManager userSubscriptions={userSubscriptions} users={users} setActiveTab={setActiveTab} success={success} onCancelSubscription={handleCancelSubscription} data-id="element-16348" />;
      case 'advertising':
        return <AdminAdvertisingFullManager adRequests={adRequests} adPlacements={[]} astrologers={astrologers} setActiveTab={setActiveTab} setDetailPanel={setDetailPanel} success={success} data-id="element-16349" />;
      case 'content':
        return <CmsManager success={success} data-id="element-16350" />;
      case 'calendar_events':
        return <AdminCalendarEventsManager calendarEvents={calendarEvents} setCreatePanel={setCreatePanel} handlePublishEvent={handlePublishEvent} renderTableHeader={renderTableHeader} generateCSV={generateCSV} data-id="element-16351" />;
      case 'marketing':
        return <AdminMarketingManager promoCodes={promoCodes} banners={banners} setCreatePanel={setCreatePanel} success={success} handleDeletePromo={handleDeletePromo} handleDeleteBanner={handleDeleteBanner} renderTableHeader={renderTableHeader} generateCSV={generateCSV} data-id="element-16352" />;
      case 'services':
        return <AdminServicesManager services={services} setCreatePanel={setCreatePanel} handleDeleteService={handleDeleteService} renderTableHeader={renderTableHeader} generateCSV={generateCSV} data-id="element-16353" />;
      case 'notifications':
        return <AdminNotificationsCenter success={success} data-id="element-16354" />;
      case 'full_chat':
        return <AdminFullChat success={success} data-id="element-16355" />;
      case 'messages':
        return <AdminMessaging tickets={tickets} setTickets={setTickets} success={success} data-id="element-16356" />;
      case 'moderation':
        return <AdminModerationCenter complaints={complaints} reviews={reviews} setActiveTab={setActiveTab} setDetailPanel={setDetailPanel} success={success} data-id="element-16357" />;
      case 'analytics':
        return <AnalyticsDashboard orders={orders} users={users} astrologers={astrologers} trackingCodes={trackingCodes} setActiveTab={setActiveTab} showTrackingSetup={showTrackingSetup} setShowTrackingSetup={setShowTrackingSetup} updateTrackingCode={updateTrackingCode} success={success} selectedPeriod={selectedPeriod} data-id="element-16358" />;
      case 'settings':
        return <AdminSettingsFullManager notifSettings={notifSettings} setNotifSettings={setNotifSettings} paymentSystems={paymentSystems} setPaymentSystems={setPaymentSystems} trackingCodes={trackingCodes} updateTrackingCode={updateTrackingCode} showTrackingSetup={showTrackingSetup} setShowTrackingSetup={setShowTrackingSetup} success={success} data-id="element-16359" />;
      case 'roles':
        return <AdminRolesManager success={success} data-id="element-16360" />;
      case 'audit':
        return <AdminAuditLog data-id="element-16361" />;
      case 'marketplace':
        return <AdminMarketplaceManager success={success} data-id="element-16362" />;
      case 'system_health':
        return <AdminSystemHealth success={success} setActiveTab={setActiveTab} data-id="element-16363" />;
      default:
        return <div className="text-center py-20 text-slate-500" data-id="element-16364">
            Раздел «{activeTab}» в разработке
          </div>;
    }
  };
  // --- SIDEBAR NAV ---
  const navGroups = [{
    label: 'ГЛАВНОЕ',
    items: [{
      id: 'dashboard',
      label: 'Dashboard',
      icon: <LayoutDashboard className="w-4 h-4" data-id="element-16365" />
    }, {
      id: 'users',
      label: 'Пользователи',
      icon: <Users className="w-4 h-4" data-id="element-16366" />
    }, {
      id: 'astrologers',
      label: 'Астрологи',
      icon: <Star className="w-4 h-4" data-id="element-16367" />
    }, {
      id: 'verification',
      label: 'Верификация',
      icon: <ShieldCheck className="w-4 h-4" data-id="element-16368" />,
      badge: verificationQueue.filter(v => v.status === 'pending').length
    }]
  }, {
    label: 'ПРОДАЖИ И УСЛУГИ',
    items: [{
      id: 'orders',
      label: 'Заказы',
      icon: <ShoppingBag className="w-4 h-4" data-id="element-16369" />,
      badge: orders.filter(o => o.status === 'in_progress').length
    }, {
      id: 'marketplace',
      label: 'Маркетплейс',
      icon: <Store className="w-4 h-4" data-id="element-16370" />
    }, {
      id: 'consultations',
      label: 'Консультации',
      icon: <Video className="w-4 h-4" data-id="element-16371" />
    }, {
      id: 'ai_reports',
      label: 'AI / ИИ-отчёты',
      icon: <Zap className="w-4 h-4" data-id="element-16372" />
    }, {
      id: 'finance',
      label: 'Финансы',
      icon: <DollarSign className="w-4 h-4" data-id="element-16373" />
    }, {
      id: 'subscriptions',
      label: 'Подписки',
      icon: <Repeat className="w-4 h-4" data-id="element-16374" />
    }]
  }, {
    label: 'МАРКЕТИНГ И КОНТЕНТ',
    items: [{
      id: 'advertising',
      label: 'Реклама и размещения',
      icon: <Megaphone className="w-4 h-4" data-id="element-16375" />
    }, {
      id: 'content',
      label: 'Контент / CMS',
      icon: <FileText className="w-4 h-4" data-id="element-16376" />
    }, {
      id: 'calendar_events',
      label: 'Календарь',
      icon: <Calendar className="w-4 h-4" data-id="element-16377" />
    }]
  }, {
    label: 'ПОДДЕРЖКА',
    items: [{
      id: 'notifications',
      label: 'Уведомления',
      icon: <Bell className="w-4 h-4" data-id="element-16378" />,
      badge: 5
    }, {
      id: 'full_chat',
      label: 'Чат с пользователями',
      icon: <MessageSquare className="w-4 h-4" data-id="element-16379" />,
      badge: 3
    }, {
      id: 'messages',
      label: 'Тикеты / Поддержка',
      icon: <FileText className="w-4 h-4" data-id="element-16380" />,
      badge: 4
    }, {
      id: 'moderation',
      label: 'Модерация',
      icon: <Shield className="w-4 h-4" data-id="element-16381" />,
      badge: complaints.filter(c => c.status === 'open').length
    }]
  }, {
    label: 'СИСТЕМА',
    items: [{
      id: 'analytics',
      label: 'Аналитика',
      icon: <BarChart3 className="w-4 h-4" data-id="element-16382" />
    }, {
      id: 'system_health',
      label: 'Здоровье системы',
      icon: <HeartPulse className="w-4 h-4" data-id="element-16383" />
    }, {
      id: 'settings',
      label: 'Настройки',
      icon: <Settings className="w-4 h-4" data-id="element-16384" />
    }, {
      id: 'roles',
      label: 'Роли и доступы',
      icon: <Shield className="w-4 h-4" data-id="element-16385" />
    }, {
      id: 'audit',
      label: 'Системные логи / Audit',
      icon: <Activity className="w-4 h-4" data-id="element-16386" />
    }]
  }];
  const toggleGroup = (label: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);else next.add(label);
      return next;
    });
  };
  return <div className="min-h-screen bg-slate-50 flex" data-id="element-16387">
      {/* SIDEBAR */}
      <div className="w-64 bg-slate-900 text-slate-300 flex flex-col flex-shrink-0 h-screen sticky top-0" data-id="element-16388">
        <div className="p-4 border-b border-slate-800 flex items-center gap-3" data-id="element-16389">
          <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-purple-600 rounded flex items-center justify-center mr-2 shadow-md" data-id="element-16390">
            <span className="text-white font-bold text-xs" data-id="element-16391">A</span>
          </div>
          <span className="font-bold text-sm tracking-tight text-white" data-id="element-16392">
            Admin Panel
          </span>
        </div>
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden" data-id="element-16393">
          <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1 scrollbar-thin" data-id="element-16394">
            {navGroups.map(group => <div key={group.label} className="mb-2" data-id="element-16395">
                <button className="flex items-center justify-between w-full px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors" onClick={() => toggleGroup(group.label)} data-id="element-16396">
                  {group.label}
                  {collapsedGroups.has(group.label) ? <ChevronRight className="w-3 h-3" data-id="element-16397" /> : <ChevronDown className="w-3 h-3" data-id="element-16398" />}
                </button>
                {!collapsedGroups.has(group.label) && group.items.map(item => <button key={item.id} onClick={() => setActiveTab(item.id)} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${activeTab === item.id ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`} data-id="element-16399">
                      {item.icon}
                      <span className="flex-1 text-left truncate" data-id="element-16400">
                        {item.label}
                      </span>
                      {'badge' in item && item.badge ? <span className="bg-rose-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center flex-shrink-0" data-id="element-16401">
                          {item.badge}
                        </span> : null}
                    </button>)}
              </div>)}
          </nav>
        </div>
        <div className="p-3 border-t border-slate-800 flex-shrink-0" data-id="element-16402">
          <button onClick={() => {
          logout();
          onNavigate('home');
        }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" data-id="element-16403">
            <LogOut className="w-4 h-4" data-id="element-16404" />
            <span data-id="element-16405">Выйти</span>
          </button>
        </div>
      </div>

      {/* MAIN */}
      <div className="flex-1 flex flex-col min-w-0" data-id="element-16406">
        {/* TOPBAR */}
        <div className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-30" data-id="element-16407">
          <div className="flex items-center gap-3 flex-1 max-w-md relative" data-id="element-16408">
            <div className="flex items-center gap-2.5 flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50/80 focus-within:border-indigo-300 focus-within:bg-white focus-within:ring-2 focus-within:ring-indigo-100 transition-all" data-id="element-16409">
              <Search className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-16410" />
              <input type="text" placeholder="Глобальный поиск..." className="flex-1 text-sm border-none outline-none bg-transparent text-slate-700 placeholder-slate-400" value={globalSearch} onChange={e => {
              setGlobalSearch(e.target.value);
              setShowGlobalResults(e.target.value.length > 0);
            }} onFocus={() => {
              if (globalSearch.length > 0) setShowGlobalResults(true);
            }} onBlur={() => setTimeout(() => setShowGlobalResults(false), 200)} data-id="element-16411" />
              <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 bg-white rounded border border-slate-200" data-id="element-16412">
                ⌘ K
              </kbd>
            </div>
            {/* Global search results dropdown */}
            {showGlobalResults && globalSearch.trim() && <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-id="element-16413">
                <div className="p-2 space-y-0.5" data-id="element-16414">
                  {navGroups.flatMap(g => g.items).filter(item => item.label.toLowerCase().includes(globalSearch.toLowerCase()) || item.id.toLowerCase().includes(globalSearch.toLowerCase())).slice(0, 6).map(item => <button key={item.id} onClick={() => {
                setActiveTab(item.id);
                setGlobalSearch('');
                setShowGlobalResults(false);
              }} className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-16415">
                        {item.icon}
                        <span data-id="element-16416">{item.label}</span>
                      </button>)}
                  {navGroups.flatMap(g => g.items).filter(item => item.label.toLowerCase().includes(globalSearch.toLowerCase()) || item.id.toLowerCase().includes(globalSearch.toLowerCase())).length === 0 && <div className="px-3 py-4 text-center text-sm text-slate-400" data-id="element-16417">
                      Ничего не найдено
                    </div>}
                </div>
              </div>}
          </div>
          <div className="flex items-center gap-2" data-id="element-16418">
            <div className="hidden lg:flex items-center gap-1 bg-slate-50 rounded-lg p-1 border border-slate-200" data-id="element-16419">
              {(['Сегодня', 'Вчера', '7 дней', '30 дней', 'Квартал', 'Год'] as const).map(period => <button key={period} onClick={() => setSelectedPeriod(period)} className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors whitespace-nowrap ${selectedPeriod === period ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-16420">
                  {period}
                </button>)}
            </div>
            <Button variant="primary" size="sm" icon={<Plus className="w-4 h-4" data-id="element-16422" />} className="hidden sm:flex" onClick={() => setCreatePanel({
            type: 'astrologer',
            title: 'Добавить астролога'
          })} data-id="element-16421">
              Создать
            </Button>
            {/* Notifications */}
            <div className="relative" data-id="element-16423">
              <button className="relative p-2 text-slate-400 hover:text-slate-600 transition-colors rounded-full hover:bg-slate-100" onClick={() => {
              setShowNotifications(!showNotifications);
              setShowUserMenu(false);
            }} data-id="element-16424">
                <Bell className="w-4 h-4" data-id="element-16425" />
                <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-rose-500 rounded-full" data-id="element-16426"></span>
              </button>
              {showNotifications && <div className="absolute top-full right-0 mt-1.5 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-id="element-16427">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between" data-id="element-16428">
                    <span className="text-sm font-bold text-slate-900" data-id="element-16429">
                      Уведомления
                    </span>
                    <span className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full" data-id="element-16430">
                      3 новых
                    </span>
                  </div>
                  <div className="max-h-64 overflow-y-auto" data-id="element-16431">
                    {[{
                  icon: <ShoppingBag className="w-4 h-4 text-emerald-500" data-id="element-16432" />,
                  text: 'Новый заказ ORD-1847 от Анна К.',
                  time: '2 мин назад',
                  unread: true
                }, {
                  icon: <ShieldCheck className="w-4 h-4 text-amber-500" data-id="element-16433" />,
                  text: 'Заявка на верификацию от Мария З.',
                  time: '15 мин назад',
                  unread: true
                }, {
                  icon: <AlertTriangle className="w-4 h-4 text-rose-500" data-id="element-16434" />,
                  text: 'Жалоба на отзыв #4521',
                  time: '1 час назад',
                  unread: true
                }, {
                  icon: <CheckCircle2 className="w-4 h-4 text-slate-400" data-id="element-16435" />,
                  text: 'Отчёт RPT-0892 сгенерирован',
                  time: '3 часа назад',
                  unread: false
                }, {
                  icon: <DollarSign className="w-4 h-4 text-slate-400" data-id="element-16436" />,
                  text: 'Выплата астрологу Алексей Л.',
                  time: 'Вчера',
                  unread: false
                }].map((n, i) => <button key={i} className={`flex items-start gap-3 w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors ${n.unread ? 'bg-indigo-50/30' : ''}`} onClick={() => setShowNotifications(false)} data-id="element-16437">
                        <div className="mt-0.5 flex-shrink-0" data-id="element-16438">{n.icon}</div>
                        <div className="flex-1 min-w-0" data-id="element-16439">
                          <p className={`text-xs ${n.unread ? 'font-semibold text-slate-900' : 'text-slate-600'}`} data-id="element-16440">
                            {n.text}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5" data-id="element-16441">
                            {n.time}
                          </p>
                        </div>
                        {n.unread && <div className="w-2 h-2 rounded-full bg-indigo-500 mt-1.5 flex-shrink-0" data-id="element-16442" />}
                      </button>)}
                  </div>
                  <div className="px-4 py-2.5 border-t border-slate-100" data-id="element-16443">
                    <button onClick={() => {
                  setActiveTab('notifications');
                  setShowNotifications(false);
                }} className="w-full text-center text-xs font-medium text-indigo-600 hover:text-indigo-700 transition-colors" data-id="element-16444">
                      Все уведомления →
                    </button>
                  </div>
                </div>}
            </div>
            {/* User menu */}
            <div className="relative" data-id="element-16445">
              <button className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors" onClick={() => {
              setShowUserMenu(!showUserMenu);
              setShowNotifications(false);
            }} data-id="element-16446">
                <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs" data-id="element-16447">
                  АД
                </div>
              </button>
              {showUserMenu && <div className="absolute top-full right-0 mt-1.5 w-56 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-id="element-16448">
                  <div className="px-4 py-3 border-b border-slate-100" data-id="element-16449">
                    <div className="text-sm font-bold text-slate-900" data-id="element-16450">
                      Администратор
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-16451">
                      admin@astriqo.com
                    </div>
                  </div>
                  <div className="p-1.5" data-id="element-16452">
                    <button onClick={() => {
                  setActiveTab('settings');
                  setShowUserMenu(false);
                }} className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-16453">
                      <Settings className="w-4 h-4 text-slate-400" data-id="element-16454" />
                      Настройки
                    </button>
                    <button onClick={() => {
                  setActiveTab('roles');
                  setShowUserMenu(false);
                }} className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-16455">
                      <Shield className="w-4 h-4 text-slate-400" data-id="element-16456" />
                      Роли и доступы
                    </button>
                    <button onClick={() => {
                  setActiveTab('audit');
                  setShowUserMenu(false);
                }} className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-16457">
                      <Activity className="w-4 h-4 text-slate-400" data-id="element-16458" />
                      Логи
                    </button>
                    <div className="h-px bg-slate-100 my-1" data-id="element-16459" />
                    <button onClick={() => {
                  logout();
                  onNavigate('home');
                }} className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm text-rose-600 hover:bg-rose-50 transition-colors" data-id="element-16460">
                      <LogOut className="w-4 h-4 text-rose-400" data-id="element-16461" />
                      Выйти
                    </button>
                  </div>
                </div>}
            </div>
            {/* Site button - styled */}
            <button onClick={() => onNavigate('home')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 shadow-sm hover:shadow-md transition-all" data-id="element-16462">
              <Globe className="w-3.5 h-3.5" data-id="element-16463" />
              <span data-id="element-16464">Сайт</span>
              <ExternalLink className="w-3 h-3 opacity-70" data-id="element-16465" />
            </button>
          </div>
        </div>

        {/* CONTENT */}
        <div className="flex-1 p-6" data-id="element-16466">
          <AdminSectionBoundary key={activeTab} sectionName={activeTab} sectionLabel={navGroups.flatMap(g => g.items).find(i => i.id === activeTab)?.label || activeTab} data-id="element-16467">
            <Suspense fallback={<div className="flex items-center justify-center py-20" data-id="element-16469">
                  <div className="flex flex-col items-center gap-3" data-id="element-16470">
                    <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-16471" />
                    <span className="text-sm text-slate-500" data-id="element-16472">
                      Загрузка раздела...
                    </span>
                  </div>
                </div>} data-id="element-16468">
              {renderContent()}
            </Suspense>
          </AdminSectionBoundary>
        </div>
      </div>

      {/* PANELS */}
      <AdminDetailPanel detailPanel={detailPanel} setDetailPanel={setDetailPanel} data-id="element-16473" />
      <AdminCreatePanel createPanel={createPanel} setCreatePanel={setCreatePanel} newContentForm={newContentForm} setNewContentForm={setNewContentForm} handleCreateContent={handleCreateContent} newPromoForm={newPromoForm} setNewPromoForm={setNewPromoForm} handleCreatePromo={handleCreatePromo} newCampaignForm={newCampaignForm} setNewCampaignForm={setNewCampaignForm} handleCreateCampaign={handleCreateCampaign} newAstrologerForm={newAstrologerForm} setNewAstrologerForm={setNewAstrologerForm} handleCreateAstrologer={handleCreateAstrologer} newOrderForm={newOrderForm} setNewOrderForm={setNewOrderForm} handleCreateOrder={handleCreateOrder} newAiReportForm={newAiReportForm} setNewAiReportForm={setNewAiReportForm} handleCreateAiReport={handleCreateAiReport} newAdForm={newAdForm} setNewAdForm={setNewAdForm} handleCreateAd={handleCreateAd} newEventForm={newEventForm} setNewEventForm={setNewEventForm} handleCreateEvent={handleCreateEvent} newServiceForm={newServiceForm} setNewServiceForm={setNewServiceForm} handleCreateService={handleCreateService} success={success} data-id="element-16474" />
    </div>;
}